/*
 * The preview's payroll, time and My pay (draft 4), worked out with the same shared rules as the real server
 * (@steward/shared payroll.ts). The made-up visits of the last four weeks count as paid the day after they were done (two
 * with a tip, one where the crew kept the cash); the last two days' visits are waiting on payment. The week before last is
 * marked paid, and a tip that came in after it lands in last week. This is the preview's own story: it does not follow the
 * preview's invoices, except invoices typed by hand (handWork), which pay who the office says worked them. Nothing is
 * saved; a reload starts over. Two-week pay and the My pay switch follow the preview's settings and Team.
 */
import {
  addDays,
  capClockOut,
  ifEveryonePaid,
  isEarly,
  isPeriodStart,
  myPayDetail,
  myPayWeeks,
  payPatch,
  Payroll,
  payCalendar,
  periodEnd,
  periodOf,
  periodToShow,
  shiftInput,
  shiftMinutes,
  shortName,
  clockBody,
  undoBlockedBy,
  weekOf,
  type Action,
  type ClockWhere,
  type PayCalendar,
  type PaidRun,
  type PaidWork,
  type PayPerson,
  type PayWeekAnswer,
  type Role,
  type Shift,
  type ShiftRow,
  type WaitingWork,
} from "@steward/shared";
import { handWork } from "./money";
import { workLookup, type WorkPerson } from "./work";

interface Answer {
  status: number;
  body: unknown;
}
const ok = (body: unknown, status = 200): Answer => ({ status, body });
const fail = (status: number, code: string, message: string, extra: Record<string, unknown> = {}): Answer => ({
  status,
  body: { code, message, ...extra },
});
const NOT_ALLOWED = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");

export interface PayrollCtx {
  companyId: string;
  role: Role;
  gate: (a: Action) => boolean;
  people: WorkPerson[];
  /** The signed-in login's person here, or null. */
  personId: string | null;
  paydayWeekday: number;
  defaultPoolPercent: number | null;
  commercialPoolPercent: number | null;
  periodWeeks: number;
  twoWeekFrom: string | null;
  log: (action: string) => void;
}

interface StoredShift extends Shift {
  companyId: string;
  source: "app" | "office";
  edited: boolean;
  note: string | null;
  inWhere?: ClockWhere | null;
  outWhere?: ClockWhere | null;
}

/** Made-up spots in New Orleans for the preview's clocked shifts. */
const spot = (n: number): ClockWhere => ({
  lat: 29.95 + (n % 7) * 0.004,
  lng: -90.07 - (n % 5) * 0.005,
  accuracyM: 10 + (n % 4) * 5,
});

const pay = new Map<string, PayPerson>();
const work: (PaidWork & { companyId: string })[] = [];
const waiting: (WaitingWork & { companyId: string })[] = [];
const shifts: StoredShift[] = [];
const runs = new Map<string, Map<string, PaidRun>>();
let TODAY = "";
let newId: () => string = () => crypto.randomUUID();

/** Wall time in New Orleans as an instant (the preview is always in Central time; close enough for made-up data). */
const at = (date: string, hhmm: string) => {
  const guess = Date.parse(`${date}T${hhmm}:00Z`);
  const off = new Intl.DateTimeFormat("en-US", { timeZone: "America/Chicago", hourCycle: "h23", hour: "2-digit" }).format(guess);
  return guess + ((Number(hhmm.slice(0, 2)) - Number(off) + 24) % 24) * 3_600_000;
};
const hhmmOf = (ms: number) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hour12: false }).format(ms);

export function seedPayroll(opts: { id: () => string; today: string; companyId: string; people: WorkPerson[] }): void {
  newId = opts.id;
  TODAY = opts.today;
  const cid = opts.companyId;
  const team = opts.people.filter((p) => p.companyId === cid);
  const percents = [40, 35, 30, 30];
  let crewN = 0;
  for (const p of team) {
    if (p.role === "crew" || p.role === "owner") {
      pay.set(p.id, { payType: "percent", percent: percents[crewN] ?? 30, hourlyRateCents: null, active: p.active });
      crewN += 1;
    } else
      pay.set(p.id, { payType: "hourly", percent: null, hourlyRateCents: p.role === "manager" ? 2200 : 1800, active: p.active });
  }
  const done = workLookup.doneVisits(cid).sort((a, b) => a.date.localeCompare(b.date));
  done.forEach((v, i) => {
    const base = v.lines.reduce((s, l) => s + l.amountCents, 0);
    if (v.date >= addDays(TODAY, -2)) {
      waiting.push({
        companyId: cid,
        visitId: v.visitId,
        date: v.date,
        clientName: v.clientName,
        baseCents: base,
        crew: v.crew,
        poolPercent: null,
      });
      return;
    }
    const paidOn = addDays(v.date, 1);
    const entry = { companyId: cid, visitId: v.visitId, date: v.date, clientName: v.clientName, crew: v.crew, poolPercent: null };
    work.push({ ...entry, on: paidOn, baseCents: base, tipCents: 0, crewCollected: i === 3 });
    if (i % 5 === 1) work.push({ ...entry, on: paidOn, baseCents: 0, tipCents: 1500 + (i % 3) * 500, crewCollected: false });
  });
  // Shifts: the hourly office people, weekdays of the last three weeks, 8 to 4:30.
  for (const p of team.filter((x) => x.active && pay.get(x.id)?.payType === "hourly"))
    for (let d = -21; d < 0; d += 1) {
      const date = addDays(TODAY, d);
      const dow = new Date(`${date}T12:00:00Z`).getUTCDay();
      if (dow === 0 || dow === 6) continue;
      shifts.push({
        companyId: cid,
        id: newId(),
        personId: p.id,
        date,
        clockIn: at(date, "08:00"),
        clockOut: at(date, "16:30"),
        source: "app",
        edited: false,
        note: null,
        inWhere: spot(d + 21),
        outWhere: spot(d + 24),
      });
    }
  // The week before last is paid; then a late tip comes in for one of its jobs.
  const paidWeek = addDays(weekOf(TODAY), -14);
  const book = bookOf(cid);
  const first = new Payroll(book).week(paidWeek).jobs[0];
  runsOf(cid).set(paidWeek, new Payroll(book).snapshot(paidWeek, `${addDays(paidWeek, 11)}T15:00:00.000Z`, "Olivia G."));
  if (first) {
    const w = work.find((x) => x.visitId === first.visitId)!;
    work.push({ ...w, on: addDays(paidWeek, 8), baseCents: 0, tipCents: 2000 });
  }
}

const runsOf = (cid: string) => {
  let r = runs.get(cid);
  if (!r) {
    r = new Map();
    runs.set(cid, r);
  }
  return r;
};

let settingsNow = {
  paydayWeekday: 5,
  defaultPoolPercent: null as number | null,
  commercialPoolPercent: 50 as number | null,
  periodWeeks: 1,
  twoWeekFrom: null as string | null,
};
const calNow = (): PayCalendar => payCalendar(settingsNow.periodWeeks, settingsNow.twoWeekFrom);

/**
 * The server's guard on changing pay periods (apps/api/src/payroll/calendar.ts): two-week pay starts after the last paid
 * week, and nothing changes once a two-week period is paid. Answers the problem in plain words, or null.
 */
export function calendarProblem(
  cid: string,
  now: { periodWeeks: number; twoWeekFrom: string | null },
  weeks: number,
  from: string | null,
): { code: string; message: string } | null {
  const was = payCalendar(now.periodWeeks, now.twoWeekFrom);
  const next = payCalendar(weeks, from);
  if (was.weeks === next.weeks && (was.weeks === 1 || was.from === next.from)) return null;
  const rs = [...runsOf(cid).keys()].map((start) => ({ start, end: periodEnd(start, was) }));
  if (rs.some((r) => r.end > addDays(r.start, 6)))
    return {
      code: "two_week_paid",
      message: "A two-week pay period is already marked paid, so this cannot change now. Ask head office for help.",
    };
  const last = rs.reduce((a, r) => (r.end > a ? r.end : a), "");
  if (next.weeks === 2 && last && next.from <= last)
    return { code: "after_last_paid", message: "Two-week pay has to start after the last paid week. Pick a later Monday." };
  return null;
}

/** People whose My pay switch is off on Team (question 50). */
const myPayOff = new Set<string>();
export const showsMyPay = (personId: string) => !myPayOff.has(personId);
export function setShowsMyPay(personId: string, on: boolean): void {
  if (on) myPayOff.delete(personId);
  else myPayOff.add(personId);
}

function bookOf(cid: string) {
  const people: Record<string, PayPerson> = {};
  for (const [id, p] of pay) people[id] = p;
  return {
    work: [...work.filter((w) => w.companyId === cid), ...handWork(cid).paid],
    shifts: shifts.filter((s) => s.companyId === cid),
    runs: runsOf(cid),
    settings: {
      defaultPoolPercent: settingsNow.defaultPoolPercent,
      commercialPoolPercent: settingsNow.commercialPoolPercent,
      people,
    },
    paydayWeekday: settingsNow.paydayWeekday,
    calendar: calNow(),
    now: Date.now(),
  };
}

const nameOf = (ctx: PayrollCtx, id: string) => {
  const p = ctx.people.find((x) => x.id === id);
  return p ? shortName(p.firstName, p.lastName) : "Removed person";
};

const rowOf = (ctx: PayrollCtx, s: StoredShift): ShiftRow => ({
  id: s.id,
  personId: s.personId,
  personName: nameOf(ctx, s.personId),
  date: s.date,
  clockIn: new Date(s.clockIn).toISOString(),
  clockOut: s.clockOut === null ? null : new Date(s.clockOut).toISOString(),
  inTime: hhmmOf(s.clockIn),
  outTime: s.clockOut === null ? null : hhmmOf(s.clockOut),
  minutes: shiftMinutes(s, Date.now()),
  source: s.source,
  edited: s.edited,
  note: s.note,
  inWhere: s.inWhere ?? null,
  outWhere: s.outWhere ?? null,
});

function weekAnswer(ctx: PayrollCtx, start: string): PayWeekAnswer {
  const book = bookOf(ctx.companyId);
  const view = new Payroll(book).week(start);
  const cal = calNow();
  const cur = periodOf(TODAY, cal);
  const end = periodEnd(start, cal);
  const proj = ifEveryonePaid(
    [...waiting, ...handWork(ctx.companyId).waiting].filter((w) => w.companyId === ctx.companyId && w.date <= end),
    book.settings,
  );
  return {
    ...view,
    state: view.paid ? "paid" : start === cur ? "current" : start > cur ? "future" : "open",
    days: end === addDays(start, 13) ? 14 : 7,
    early: !view.paid && start <= cur && isEarly(start, TODAY, cal),
    undoBlockedBy: view.paid ? undoBlockedBy(book.runs.values(), start) : null,
    people: view.people.map((p) => ({ ...p, name: nameOf(ctx, p.personId) })),
    waiting: { ...proj, people: proj.people.map((p) => ({ ...p, name: nameOf(ctx, p.personId) })) },
    settings: { ...settingsNow, twoWeekFrom: cal.weeks === 2 ? cal.from : null },
    missingRates: ctx.people
      .filter((p) => p.active && pay.get(p.id)?.payType === "hourly" && !pay.get(p.id)?.hourlyRateCents)
      .map((p) => shortName(p.firstName, p.lastName)),
    shifts: shifts.filter((s) => s.companyId === ctx.companyId && s.date >= start && s.date <= end).map((s) => rowOf(ctx, s)),
  };
}

const openShiftOf = (cid: string, personId: string) =>
  shifts.find((s) => s.companyId === cid && s.personId === personId && s.clockOut === null);

function clockAnswer(ctx: PayrollCtx) {
  if (!ctx.personId) return { on: false, shift: null, todayMinutes: 0, linked: false, liveGps: true };
  const open = openShiftOf(ctx.companyId, ctx.personId);
  const todayMinutes = shifts
    .filter((s) => s.companyId === ctx.companyId && s.personId === ctx.personId && (s.date === TODAY || s === open))
    .reduce((a, s) => a + shiftMinutes(s, Date.now()), 0);
  return { on: Boolean(open), shift: open ? rowOf(ctx, open) : null, todayMinutes, linked: true, liveGps: true };
}

/** Answers the draft 4 routes, or null when the route is not one of them. */
export function payrollRoute(ctx: PayrollCtx, method: string, p: string, query: URLSearchParams, b: unknown): Answer | null {
  settingsNow = {
    paydayWeekday: ctx.paydayWeekday,
    defaultPoolPercent: ctx.defaultPoolPercent,
    commercialPoolPercent: ctx.commercialPoolPercent,
    periodWeeks: ctx.periodWeeks,
    twoWeekFrom: ctx.twoWeekFrom,
  };
  const route = `${method} ${p}`;
  const cid = ctx.companyId;
  const parts = p.split("/");
  const cal = calNow();
  const badWeek = () =>
    fail(
      400,
      "bad_week",
      cal.weeks === 2 ? "Pick the first Monday of a pay period." : "Pay weeks start on a Monday. Pick a Monday.",
    );

  if (route === "GET payroll") {
    if (!ctx.gate("payroll.read")) return NOT_ALLOWED();
    const start = query.get("start") ?? periodToShow(TODAY, cal);
    if (!isPeriodStart(start, cal)) return badWeek();
    return ok(weekAnswer(ctx, start));
  }
  if (route === "GET payroll/people") {
    if (!ctx.gate("payroll.read")) return NOT_ALLOWED();
    return ok(
      ctx.people.map((x) => {
        const y = pay.get(x.id);
        return {
          id: x.id,
          name: shortName(x.firstName, x.lastName),
          role: x.role,
          active: x.active,
          payType: y?.payType ?? "percent",
          percent: y?.percent ?? null,
          hourlyRateCents: y?.hourlyRateCents ?? null,
        };
      }),
    );
  }
  if (method === "PATCH" && parts[0] === "payroll" && parts[1] === "people" && parts[2]) {
    if (!ctx.gate("payroll.update")) return NOT_ALLOWED();
    const x = ctx.people.find((y) => y.id === parts[2]);
    const parsed = payPatch.safeParse(b);
    if (!x || !parsed.success) return fail(400, "bad_request", "Check the pay and try again.");
    const was: PayPerson = pay.get(x.id) ?? { payType: "percent", percent: null, hourlyRateCents: null, active: x.active };
    const d = parsed.data;
    const next: PayPerson = {
      payType: d.payType ?? was.payType,
      percent: d.percent !== undefined ? d.percent : was.percent,
      hourlyRateCents: d.hourlyRateCents !== undefined ? d.hourlyRateCents : was.hourlyRateCents,
      active: was.active,
    };
    if (next.payType === "percent" && !(next.percent && next.percent > 0))
      return fail(400, "bad_request", "Type their percent of each job, from 1 to 100.");
    if (next.payType === "hourly" && !(next.hourlyRateCents && next.hourlyRateCents > 0))
      return fail(400, "bad_request", "Type their pay for each hour.");
    pay.set(x.id, next);
    ctx.log("payroll.pay_change");
    const { active: _a, ...row } = next;
    return ok({ id: x.id, name: shortName(x.firstName, x.lastName), role: x.role, active: x.active, ...row });
  }
  if (method === "POST" && parts[0] === "payroll" && parts[1] && (parts[2] === "paid" || parts[2] === "undo")) {
    if (!ctx.gate("payroll.update")) return NOT_ALLOWED();
    const start = parts[1];
    if (!isPeriodStart(start, cal)) return badWeek();
    const rs = runsOf(cid);
    if (parts[2] === "paid") {
      if (start > periodOf(TODAY, cal))
        return fail(409, "week_ahead", "That week has not started yet. Mark it paid once it has.");
      const early = typeof b === "object" && b !== null && "early" in b && b.early === true;
      if (!rs.has(start) && isEarly(start, TODAY, cal) && !early)
        return fail(409, "paid_early", "This pay period is not over yet. Say you mean to pay it early to mark it paid now.");
      if (!rs.has(start)) rs.set(start, new Payroll(bookOf(cid)).snapshot(start, new Date().toISOString(), "You"));
      ctx.log("payroll.paid");
    } else if (rs.has(start)) {
      const later = undoBlockedBy(rs.values(), start);
      if (later)
        return fail(409, "paid_later", "A later paid week holds money from this week. Undo that week first.", { week: later });
      rs.delete(start);
      ctx.log("payroll.undo");
    }
    return ok(weekAnswer(ctx, start));
  }

  if (route === "GET shifts") {
    if (!ctx.gate("payroll.read")) return NOT_ALLOWED();
    const from = query.get("from") ?? TODAY;
    const to = query.get("to") ?? TODAY;
    return ok(
      shifts
        .filter((s) => s.companyId === cid && s.date >= from && s.date <= to)
        .sort((a, z) => a.clockIn - z.clockIn)
        .map((s) => rowOf(ctx, s)),
    );
  }
  if (method === "GET" && parts[0] === "shifts" && parts[2] === "activity") {
    if (!ctx.gate("payroll.read")) return NOT_ALLOWED();
    const s = shifts.find((x) => x.companyId === cid && x.id === parts[1]);
    if (!s) return fail(404, "not_found", "That time was not found.");
    const r = rowOf(ctx, s);
    const plus = (mins: number) => {
      const t = new Date(s.clockIn + mins * 60_000);
      return { at: t.toISOString(), time: `${String(t.getHours()).padStart(2, "0")}:${String(t.getMinutes()).padStart(2, "0")}` };
    };
    const ev = (mins: number, kind: string, text: string) => ({
      ...plus(mins),
      kind,
      text,
      visitRef: null,
      where: null,
      off: false,
    });
    return ok([
      { ...plus(0), kind: "in", text: "Clocked in", visitRef: null, where: r.inWhere, off: false },
      ev(20, "onmyway", "Texted On my way to the first visit"),
      ev(45, "start", "Started the first visit"),
      ev(150, "done", "Finished the first visit"),
      ...(r.clockOut
        ? [
            {
              at: r.clockOut,
              time: r.outTime ?? "",
              kind: "out",
              text: "Clocked out",
              visitRef: null,
              where: r.outWhere,
              off: false,
            },
          ]
        : [ev(Math.max(0, r.minutes), "now", "Still on the clock")]),
    ]);
  }
  if ((route === "POST shifts" || (method === "PATCH" && parts[0] === "shifts")) && parts.length <= 2) {
    if (!ctx.gate("payroll.update")) return NOT_ALLOWED();
    const parsed = shiftInput.safeParse(b);
    if (!parsed.success) return fail(400, "bad_request", parsed.error.issues[0]?.message ?? "Check the time.");
    const x = parsed.data;
    const clockIn = at(x.date, x.inTime);
    const clockOut = x.outTime ? at(x.date, x.outTime) : null;
    if (clockOut !== null && clockOut <= clockIn)
      return fail(
        400,
        "bad_time",
        "The end has to be after the start. For a shift past midnight, end it at 23:59 and add the rest on the next day.",
      );
    if ((clockOut ?? Date.now()) - clockIn > 16 * 3_600_000) return fail(400, "too_long", "A shift can be at most 16 hours.");
    const row = parts[1] ? shifts.find((s) => s.companyId === cid && s.id === parts[1]) : undefined;
    if (parts[1] && !row) return fail(404, "not_found", "That time was not found.");
    const open = clockOut === null ? openShiftOf(cid, x.personId) : undefined;
    if (open && open !== row) return fail(409, "on_clock", "They are already on the clock. Change that time instead.");
    const values = { personId: x.personId, date: x.date, clockIn, clockOut, edited: true, note: x.note ?? null };
    if (row) Object.assign(row, values);
    else shifts.push({ companyId: cid, id: newId(), source: "office", ...values });
    ctx.log(row ? "shift.change" : "shift.add");
    return ok(rowOf(ctx, row ?? shifts.at(-1)!), row ? 200 : 201);
  }
  if (method === "DELETE" && parts[0] === "shifts" && parts[1]) {
    if (!ctx.gate("payroll.update")) return NOT_ALLOWED();
    const i = shifts.findIndex((s) => s.companyId === cid && s.id === parts[1]);
    if (i < 0) return fail(404, "not_found", "That time was not found.");
    shifts.splice(i, 1);
    ctx.log("shift.delete");
    return ok({ ok: true });
  }

  if (route === "GET clock") return ctx.gate("clock.update") ? ok(clockAnswer(ctx)) : NOT_ALLOWED();
  if (route === "POST clock/in" || route === "POST clock/out") {
    if (!ctx.gate("clock.update")) return NOT_ALLOWED();
    if (!ctx.personId) return fail(409, "not_linked", "Your sign in is not on the team list yet. Ask the office to add you.");
    const open = openShiftOf(cid, ctx.personId);
    const now = Date.now();
    const sent = clockBody.safeParse(b ?? {});
    if (!sent.success) return fail(400, "bad_request", "That place is not right. Try again.");
    const where = sent.data.where;
    if (route === "POST clock/in" && !open)
      shifts.push({
        companyId: cid,
        id: newId(),
        personId: ctx.personId,
        date: TODAY,
        clockIn: now,
        clockOut: null,
        source: "app",
        edited: false,
        note: null,
        inWhere: where,
      });
    if (route === "POST clock/out" && open) {
      open.clockOut = capClockOut(open.clockIn, now);
      open.outWhere = where;
    }
    return ok(clockAnswer(ctx));
  }

  if (route === "GET my-pay-access") {
    if (!ctx.gate("mypay.read")) return NOT_ALLOWED();
    return ok({ linked: ctx.personId !== null, on: ctx.personId !== null && showsMyPay(ctx.personId) });
  }
  const myPayOffFail = () => fail(403, "my_pay_off", "My pay is turned off for you. Ask the office if you need to see your pay.");
  if (route === "GET my-pay") {
    if (!ctx.gate("mypay.read")) return NOT_ALLOWED();
    if (!ctx.personId) return ok({ linked: false, weeks: [] });
    if (!showsMyPay(ctx.personId)) return myPayOffFail();
    return ok({ linked: true, weeks: myPayWeeks(new Payroll(bookOf(cid)), ctx.personId, TODAY) });
  }
  if (method === "GET" && parts[0] === "my-pay" && parts[1]) {
    if (!ctx.gate("mypay.read")) return NOT_ALLOWED();
    if (ctx.personId && !showsMyPay(ctx.personId)) return myPayOffFail();
    const d =
      ctx.personId && isPeriodStart(parts[1], cal)
        ? myPayDetail(new Payroll(bookOf(cid)).week(parts[1]), ctx.personId, TODAY)
        : null;
    return d ? ok(d) : fail(404, "not_found", "That pay week was not found.");
  }
  return null;
}
